import React from 'react';
import { useTranslation } from 'react-i18next';

import { QuoteForecast } from '../../services/quoteToCashTypes';

import { compactMoney, formatDate, fullMoney, percent } from './format';
import { LineChart, TooltipRow } from './LineChart';
import { KpiTile, SERIES_COLORS } from './SectionCard';

/** Section 1: quotes (dollars and number) and orders by period, with orders forecast from quotes */
export const QuoteForecastSection: React.FC<{ data: QuoteForecast }> = ({
  data,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const unit = data.view === 'month' ? 'month' : 'week';
  const current = data.series.findIndex((p) => p.orderForecast !== null);
  const labels = data.series.map((p) =>
    formatDate(p.period, lang, unit === 'month' ? 'monthYear' : 'day')
  );
  const money = (value: number) => fullMoney(value, lang);
  const marker = {
    markerIndex: current >= 0 ? current : undefined,
    markerLabel: t('quoteToCash.today'),
  };

  // The model in words: a baseline (left out when 0), plus what each quote (or quote dollar) adds later
  const parts = [
    ...(data.model.baseline > 0 || data.model.terms.length === 0
      ? [
          t('quoteToCash.forecast.baseline', {
            amount: compactMoney(data.model.baseline, lang),
          }),
        ]
      : []),
    ...data.model.terms.map((term) => {
      const when = t(`quoteToCash.forecast.when.${unit}`, { count: term.lag });
      return term.input === 'quoteCount'
        ? t('quoteToCash.forecast.perQuote', {
            amount: compactMoney(term.weight, lang),
            when,
          })
        : t('quoteToCash.forecast.perQuoteDollar', {
            pct: percent(term.weight, lang),
            when,
          });
    }),
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiTile
          label={t(`quoteToCash.forecast.current.${unit}`)}
          value={compactMoney(data.currentPeriodForecast, lang)}
          detail={
            current >= 0 &&
            t('quoteToCash.forecast.bookedSoFar', {
              amount: compactMoney(data.series[current].orderActual ?? 0, lang),
            })
          }
        />
        <KpiTile
          label={t(`quoteToCash.forecast.horizon.${unit}`)}
          value={compactMoney(data.forecastHorizonTotal, lang)}
        />
        <KpiTile
          label={t('quoteToCash.forecast.ordersPerQuote')}
          value={
            data.ordersPerQuoteDollar === null
              ? t('quoteToCash.notAvailable')
              : percent(data.ordersPerQuoteDollar, lang, 0)
          }
          detail={t(`quoteToCash.forecast.ordersPerQuoteDetail.${unit}`)}
        />
      </div>
      <LineChart
        ariaLabel={t('quoteToCash.forecast.title')}
        labels={labels}
        {...marker}
        formatValue={(v) => compactMoney(v, lang)}
        formatRightValue={(v) => String(Math.round(v))}
        leftAxisTitle={t('quoteToCash.forecast.axisDollars')}
        rightAxisTitle={t('quoteToCash.forecast.axisQuotes')}
        series={[
          {
            key: 'quotes',
            label: t('quoteToCash.forecast.quotes'),
            color: SERIES_COLORS.first,
            values: data.series.map((p) => p.quoteActual),
          },
          {
            key: 'orders',
            label: t('quoteToCash.forecast.orders'),
            color: SERIES_COLORS.second,
            values: data.series.map((p) => p.orderActual),
          },
          {
            key: 'forecast',
            label: t('quoteToCash.forecast.orderForecast'),
            color: SERIES_COLORS.second,
            dashed: true,
            values: data.series.map((p) => p.orderForecast),
          },
          {
            key: 'count',
            label: t('quoteToCash.forecast.numberOfQuotesRight'),
            color: SERIES_COLORS.third,
            axis: 'right',
            values: data.series.map((p) => p.quoteCount),
          },
        ]}
        renderTooltip={(i) => {
          const point = data.series[i];
          return (
            <>
              {point.quoteActual !== null && (
                <TooltipRow
                  color={SERIES_COLORS.first}
                  value={money(point.quoteActual)}
                  label={t('quoteToCash.forecast.quotes')}
                />
              )}
              {point.quoteCount !== null && (
                <TooltipRow
                  color={SERIES_COLORS.third}
                  value={String(point.quoteCount)}
                  label={t('quoteToCash.forecast.quotesSent')}
                />
              )}
              {point.orderActual !== null && (
                <TooltipRow
                  color={SERIES_COLORS.second}
                  value={money(point.orderActual)}
                  label={
                    i === current
                      ? t('quoteToCash.forecast.actualToDate')
                      : t('quoteToCash.forecast.ordersActual')
                  }
                />
              )}
              {point.orderForecast !== null && (
                <TooltipRow
                  color={SERIES_COLORS.second}
                  dashed
                  value={money(point.orderForecast)}
                  label={t('quoteToCash.forecast.forecastFullPeriod')}
                />
              )}
              {point.quoteActual === null && point.orderForecast === null && (
                <p className="text-gray-500">{t('quoteToCash.beforeEpicor')}</p>
              )}
            </>
          );
        }}
      />

      <p className="mt-4 text-sm text-gray-600">
        <span className="font-medium text-gray-700">
          {t('quoteToCash.forecast.howTitle')}
        </span>{' '}
        {t(`quoteToCash.forecast.how.${unit}`, { formula: parts.join(' + ') })}
        {t(`quoteToCash.forecast.howFit.${unit}`)}
      </p>
    </>
  );
};
